import { useEffect, useState } from 'react';
import type { ApiWorkflowWithUsages } from '@jira/project-settings__workflow-types/src/WorkflowErrorCause.types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fetchStatusesAndWorkflows } from './fetch-statuses-and-workflows.tsx';

/**
 * Custom hook to fetch workflows for a specific project and issue type
 * Returns an object containing:
 * - The workflow data (or undefined if not yet fetched)
 * - A boolean indicating if the data is currently being loaded
 * - An error object if an error occurred during fetching (or undefined)
 */
export const useWorkflows = (
	projectId: string,
	issueTypeId: string,
): {
	workflows: ApiWorkflowWithUsages[] | undefined;
	loading: boolean;
	error: Error | undefined;
} => {
	const cloudId = useCloudId();
	const [workflows, setWorkflows] = useState<ApiWorkflowWithUsages[] | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | undefined>(undefined);

	useEffect(() => {
		const fetchData = async () => {
			if (!cloudId || !projectId || !issueTypeId) {
				setLoading(false);
				return;
			}
			try {
				const data = await fetchStatusesAndWorkflows({
					projectAndIssueTypes: [{ projectId, issueTypeId }],
					cloudId,
				});
				setWorkflows(data?.workflows);
			} catch (err) {
				setError(err instanceof Error ? err : new Error('Failed to fetch workflows'));
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, [cloudId, issueTypeId, projectId]);

	return { workflows, loading, error };
};
